import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { 
  categoryApi, 
  transactionApi, 
  recurringPaymentApi,
  checkBackendConnection,
  ApiCategory, 
  ApiTransaction,
  ApiRecurringPayment,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  CreateRecurringPaymentRequest,
  UpdateRecurringPaymentRequest,
  CreatedByUserDto,
} from '@/lib/api';
import { auth } from '@/lib/firebase';
import { toast } from 'sonner';

// Updated interfaces to match backend schema
export interface Category {
  id: string;
  name: string;
  color: string;
  type: 'expense' | 'income'; // New field to distinguish category type
  monthlyBudgetLimit?: number | null; // Optional monthly spending limit
  account_id?: string; // Optional for frontend, required for API
  created_at?: string;
  updated_at?: string;
}

export interface CategoryInfo {
  id: string;
  name: string;
  color: string;
  type: 'expense' | 'income';
  isActive: boolean;
}

export interface Transaction {
  id: string;
  amount: number;
  category: string; // Category name for display
  categoryId: string; // Category UUID for API
  description: string;
  date: string;
  paymentType?: string;
  cardNumber?: string;
  accountId?: string;
  isPeriodicPayment: boolean;
  periodicPaymentId?: string; // Maps to recurringPaymentId in API
  recurringPaymentInfo?: {
    totalPayments: number;
    completedPayments: number;
    remainingPayments: number;
    paymentSequence: number;
  };
  createdByUser?: CreatedByUserDto;
  categoryInfo?: CategoryInfo; // Full category info including deleted status
  created_at?: string;
  updated_at?: string;
  source?: string; // 'web', 'whatsapp', 'automation', 'recurring', 'import'
  businessName?: string;
  merchantName?: string;
}

export interface PeriodicPayment {
  id: string;
  paymentType?: 'periodic' | 'installment'; // Type of payment (periodic like rent, or installment like loan)
  amount: number; // Monthly payment amount
  totalAmount?: number; // Total amount for installment payments
  category: string;
  categoryColor?: string; // Color from the category object in server response
  description: string;
  color: string;
  dayOfMonth: number; // 1, 5, or 10
  firstPaymentDate: string; // Date when the first payment should be processed
  totalPayments: number;
  remainingPayments: number;
  completedPayments?: number; // Number of payments completed
  expirationDate: string;
  isActive: boolean;
}

interface ExpenseContextType {
  transactions: Transaction[];
  categories: Category[];
  periodicPayments: PeriodicPayment[];
  loading: boolean;
  error: string | null;
  authError: boolean;
  clearAuthError: () => void;
  addTransaction: (transaction: Omit<Transaction, 'id' | 'categoryId'> & { categoryId?: string }) => Promise<void>;
  updateTransaction: (id: string, transaction: Partial<Transaction>) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  addCategory: (category: Omit<Category, 'id'>) => Promise<void>;
  updateCategory: (id: string, category: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  addPeriodicPayment: (payment: Omit<PeriodicPayment, 'id'>) => Promise<void>;
  updatePeriodicPayment: (id: string, payment: Partial<PeriodicPayment>) => Promise<void>;
  deletePeriodicPayment: (id: string) => Promise<void>;
  loadAllPeriodicPayments: () => Promise<void>;
  loadActivePeriodicPayments: () => Promise<void>;
  refreshData: () => Promise<void>;
  loadTransactions: (params?: { from?: string; to?: string }) => Promise<void>;
}

export const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

// Application relies entirely on API calls - no mock data

export const ExpenseProvider = ({ children }: { children: ReactNode }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [periodicPayments, setPeriodicPayments] = useState<PeriodicPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState(false); // Flag for authentication errors
  const [useApi, setUseApi] = useState(true); // Flag to track API availability

  // Helper function to convert API category to frontend category
  const mapApiCategoryToCategory = (apiCategory: ApiCategory): Category => ({
    id: apiCategory.id,
    name: apiCategory.name,
    color: apiCategory.color,
    type: apiCategory.type, // New type field
    monthlyBudgetLimit: apiCategory.monthlyBudgetLimit ?? null, // Carry budget through
    account_id: apiCategory.accountId, // Map from camelCase to snake_case
    created_at: apiCategory.createdAt,
    updated_at: apiCategory.updatedAt,
  });

  // Helper function to convert API transaction to frontend transaction
  const mapApiTransactionToTransaction = (apiTransaction: ApiTransaction, availableCategories: Category[]): Transaction => {
    // Use category from API response if available, otherwise fallback to lookup from active categories
    const categoryFromApi = apiTransaction.category;
    const categoryFromList = availableCategories.find(cat => cat.id === apiTransaction.categoryId);
    const categoryName = categoryFromApi?.name || categoryFromList?.name || 'Unknown';
    
    return {
      id: apiTransaction.id,
      amount: apiTransaction.amount,
      category: categoryName,
      categoryId: apiTransaction.categoryId,
      description: apiTransaction.description,
      date: apiTransaction.date.split('T')[0], // Convert datetime to date string
      paymentType: apiTransaction.paymentType || 'cash',
      cardNumber: apiTransaction.cardNumber || '',
      accountId: apiTransaction.accountId,
      isPeriodicPayment: apiTransaction.isRecurring, // Map from isRecurring
      periodicPaymentId: apiTransaction.recurringPaymentId, // Map from recurringPaymentId
      recurringPaymentInfo: apiTransaction.recurringPayment ? {
        totalPayments: apiTransaction.recurringPayment.totalPayments,
        completedPayments: apiTransaction.recurringPayment.completedPayments,
        remainingPayments: apiTransaction.recurringPayment.remainingPayments,
        paymentSequence: apiTransaction.recurringPayment.paymentSequence,
      } : undefined,
      createdByUser: apiTransaction.createdByUser,
      categoryInfo: categoryFromApi ? {
        id: categoryFromApi.id,
        name: categoryFromApi.name,
        color: categoryFromApi.color,
        type: categoryFromApi.type,
        isActive: categoryFromApi.isActive,
      } : undefined,
      created_at: apiTransaction.createdAt,
      updated_at: apiTransaction.updatedAt,
      source: apiTransaction.source || 'web',
      businessName: apiTransaction.businessName || undefined,
      merchantName: apiTransaction.merchantName || undefined,
    };
  };

  // Helper function to convert API recurring payment to frontend periodic payment
  const mapApiRecurringPaymentToPeriodicPayment = (apiRecurring: ApiRecurringPayment): PeriodicPayment => {
    // Use category from API response if available, otherwise fallback to lookup
    const categoryName = apiRecurring.category?.name || 
                         categories.find(cat => cat.id === apiRecurring.categoryId)?.name || 
                         'Unknown';
    
    // Use category color from API response if available
    const categoryColor = apiRecurring.category?.color || 
                          categories.find(cat => cat.id === apiRecurring.categoryId)?.color;
    
    return {
      id: apiRecurring.id,
      paymentType: apiRecurring.paymentType,
      amount: apiRecurring.amount,
      totalAmount: apiRecurring.totalAmount,
      category: categoryName,
      categoryColor: categoryColor,
      description: apiRecurring.description,
      color: apiRecurring.color,
      dayOfMonth: apiRecurring.dayOfMonth,
      firstPaymentDate: apiRecurring.firstPaymentDate,
      totalPayments: apiRecurring.totalPayments,
      remainingPayments: apiRecurring.remainingPayments,
      completedPayments: apiRecurring.completedPayments,
      expirationDate: apiRecurring.expirationDate,
      isActive: apiRecurring.isActive,
    };
  };

  // Load data from API
  const loadData = async () => {
    setLoading(true);
    setError(null);
    setAuthError(false);

    // Check backend connection first
    const backendAvailable = await checkBackendConnection();
    
    if (!backendAvailable) {
      setError('Backend not available. Check API URL (VITE_API_BASE_URL) or try again later.');
      toast.error('Backend not available. Please ensure your backend is running.');
      setLoading(false);
      return;
    }

    try {
      // Load categories first
      const apiCategories = await categoryApi.getAll();
      const mappedCategories = apiCategories.map(mapApiCategoryToCategory);
      setCategories(mappedCategories);

      // Don't load transactions here - let individual pages load them with their own filters
      // This prevents unnecessary API calls with no filters
      
      // Load periodic payments (recurring payments) - active only by default
      const apiRecurringPayments = await recurringPaymentApi.getActive();
      const mappedPeriodicPayments = apiRecurringPayments.map(mapApiRecurringPaymentToPeriodicPayment);
      setPeriodicPayments(mappedPeriodicPayments);

      // toast.success('Connected to backend successfully!');
    } catch (err: unknown) {
      console.error('Failed to load data from API:', err);
      
      // Check if this is an authentication error
      const error = err as { isAuthError?: boolean; status?: number; message?: string };
      if (error?.isAuthError || error?.status === 401) {
        console.error('🚫 Authentication error detected - user needs to login');
        setAuthError(true);
        setError('Authentication failed. Please login again.');
        toast.error('Session expired. Redirecting to login...');
        return; // Don't fall back to mock data for auth errors
      }
      
      setError('Failed to load data from backend. Please check your backend connection.');
      setUseApi(false);
      
      // No fallback - show empty state when API fails
      setCategories([]);
      setTransactions([]);
      setPeriodicPayments([]);
      toast.error('Failed to load data from backend. Please ensure your backend is running.');
    } finally {
      setLoading(false);
    }
  };

  // Track auth initialization state and current user
  const [authInitialized, setAuthInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Wait for auth to be initialized before loading data
  useEffect(() => {
    if (!auth) {
      setAuthInitialized(true);
      return;
    }

    // For mock auth, check if user is already restored
    if (auth.currentUser) {
      setCurrentUser(auth.currentUser);
      setAuthInitialized(true);
      return;
    }

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthInitialized(true);
    });

    return () => unsubscribe?.();
  }, []);

  // Load data when auth is initialized AND user is logged in
  useEffect(() => {
    if (authInitialized && currentUser) {
      // Small delay to ensure token is ready
      const timer = setTimeout(() => {
        loadData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authInitialized, currentUser]);

  // Category methods
  const addCategory = async (category: Omit<Category, 'id'>): Promise<void> => {
    if (!useApi) {
      toast.error('Backend not available. Cannot add category.');
      return;
    }

    setLoading(true);
    try {
      const createRequest: CreateCategoryRequest = {
        name: category.name,
        color: category.color,
        type: category.type, // Include category type
        monthlyBudgetLimit: category.monthlyBudgetLimit ?? null,
      };
      
      const newApiCategory = await categoryApi.create(createRequest);
      const newCategory = mapApiCategoryToCategory(newApiCategory);
      setCategories(prev => [...prev, newCategory]);
      toast.success('Category added successfully');
    } catch (err) {
      console.error('Failed to add category:', err);
      setError('Failed to add category');
      toast.error('Failed to add category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (id: string, categoryUpdate: Partial<Category>): Promise<void> => {
    if (!useApi) {
      toast.error('Backend not available. Cannot update category.');
      return;
    }

    setLoading(true);
    try {
      const updateRequest: UpdateCategoryRequest = {};
      if (categoryUpdate.name !== undefined) {
        updateRequest.name = categoryUpdate.name;
      }
      if (categoryUpdate.color !== undefined) {
        updateRequest.color = categoryUpdate.color;
      }
      if (categoryUpdate.type !== undefined) {
        updateRequest.type = categoryUpdate.type;
      }
      if (categoryUpdate.monthlyBudgetLimit !== undefined) {
        updateRequest.monthlyBudgetLimit = categoryUpdate.monthlyBudgetLimit;
      }

      const updatedApiCategory = await categoryApi.update(id, updateRequest);
      const updatedCategory = mapApiCategoryToCategory(updatedApiCategory);
      setCategories(prev => 
        prev.map(cat => cat.id === id ? updatedCategory : cat)
      );
      toast.success('Category updated successfully');
    } catch (err) {
      console.error('Failed to update category:', err);
      setError('Failed to update category');
      toast.error('Failed to update category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id: string): Promise<void> => {
    if (!useApi) {
      toast.error('Backend not available. Cannot delete category.');
      return;
    }

    setLoading(true);
    try {
      await categoryApi.delete(id);
      setCategories(prev => prev.filter(cat => cat.id !== id));
      toast.success('Category deleted successfully');
    } catch (err) {
      console.error('Failed to delete category:', err);
      setError('Failed to delete category');
      toast.error('Failed to delete category');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Transaction methods
  const addTransaction = async (transaction: Omit<Transaction, 'id' | 'categoryId'> & { categoryId?: string }): Promise<void> => {
    const categoryId = transaction.categoryId || categories.find(cat => cat.name === transaction.category)?.id;
    
    if (!categoryId) {
      toast.error('Invalid category selected');
      throw new Error('Invalid category selected');
    }

    if (!useApi) {
      const newTransaction: Transaction = {
        ...transaction,
        id: Date.now().toString(),
        categoryId,
        paymentType: transaction.paymentType || 'cash',
        cardNumber: transaction.cardNumber || '',
      };
      setTransactions(prev => [...prev, newTransaction]);
      toast.success('Transaction added successfully');
      return;
    }

    setLoading(true);
    try {
      const createRequest: CreateTransactionRequest = {
        date: transaction.date,
        amount: transaction.amount,
        categoryId,
        description: transaction.description,
        paymentType: transaction.paymentType,
        cardNumber: transaction.cardNumber,
        recurringPaymentId: transaction.periodicPaymentId, // Map to recurringPaymentId
      };
      
      const newApiTransaction = await transactionApi.create(createRequest);
      const newTransaction = mapApiTransactionToTransaction(newApiTransaction, categories);
      setTransactions(prev => [...prev, newTransaction]);
      toast.success('Transaction added successfully');
    } catch (err) {
      console.error('Failed to add transaction:', err);
      setError('Failed to add transaction');
      toast.error('Failed to add transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTransaction = async (id: string, transactionUpdate: Partial<Transaction>): Promise<void> => {
    if (!useApi) {
      setTransactions(prev => 
        prev.map(trans => trans.id === id ? { ...trans, ...transactionUpdate } : trans)
      );
      toast.success('Transaction updated successfully');
      return;
    }

    setLoading(true);
    try {
      const updateRequest: UpdateTransactionRequest = {
        date: transactionUpdate.date,
        amount: transactionUpdate.amount,
        categoryId: transactionUpdate.categoryId,
        description: transactionUpdate.description,
        paymentType: transactionUpdate.paymentType,
        cardNumber: transactionUpdate.cardNumber,
        businessName: transactionUpdate.businessName,
      };
      
      const updatedApiTransaction = await transactionApi.update(id, updateRequest);
      const updatedTransaction = mapApiTransactionToTransaction(updatedApiTransaction, categories);
      setTransactions(prev => 
        prev.map(trans => trans.id === id ? updatedTransaction : trans)
      );
      toast.success('Transaction updated successfully');
    } catch (err) {
      console.error('Failed to update transaction:', err);
      setError('Failed to update transaction');
      toast.error('Failed to update transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTransaction = async (id: string): Promise<void> => {
    if (!useApi) {
      setTransactions(prev => prev.filter(trans => trans.id !== id));
      toast.success('Transaction deleted successfully');
      return;
    }

    setLoading(true);
    try {
      await transactionApi.delete(id);
      setTransactions(prev => prev.filter(trans => trans.id !== id));
      toast.success('Transaction deleted successfully');
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      setError('Failed to delete transaction');
      toast.error('Failed to delete transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Periodic payment methods - now integrated with API
  const addPeriodicPayment = async (payment: Omit<PeriodicPayment, 'id'>) => {
    if (!useApi) {
      // Fallback to mock data when API is not available
      const newPayment: PeriodicPayment = {
        ...payment,
        id: Date.now().toString(),
      };
      setPeriodicPayments(prev => [...prev, newPayment]);
      toast.success('Periodic payment added successfully (offline mode)');
      return;
    }

    try {
      setLoading(true);
      
      // Find category ID from category name
      const category = categories.find(cat => cat.name === payment.category);
      if (!category) {
        throw new Error('Category not found');
      }

      // Create the API request payload
      const createRequest: CreateRecurringPaymentRequest = {
        paymentType: payment.paymentType || 'periodic',
        amount: payment.amount,
        totalAmount: payment.totalAmount,
        categoryId: category.id,
        description: payment.description,
        color: payment.color,
        dayOfMonth: payment.dayOfMonth,
        firstPaymentDate: payment.firstPaymentDate,
        totalPayments: payment.totalPayments,
        expirationDate: payment.expirationDate,
        isActive: payment.isActive ?? true,
      };

      // Call the API
      const apiRecurringPayment = await recurringPaymentApi.create(createRequest);
      const newPeriodicPayment = mapApiRecurringPaymentToPeriodicPayment(apiRecurringPayment);
      
      // Update local state
      setPeriodicPayments(prev => [...prev, newPeriodicPayment]);
      toast.success('Periodic payment added successfully');
    } catch (error) {
      console.error('Failed to add periodic payment:', error);
      toast.error('Failed to add periodic payment. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePeriodicPayment = async (id: string, paymentUpdate: Partial<PeriodicPayment>) => {
    if (!useApi) {
      // Fallback to mock data when API is not available
      setPeriodicPayments(prev => 
        prev.map(payment => payment.id === id ? { ...payment, ...paymentUpdate } : payment)
      );
      toast.success('Periodic payment updated successfully (offline mode)');
      return;
    }

    try {
      setLoading(true);
      
      // Create the API request payload
      const updateRequest: UpdateRecurringPaymentRequest = {};
      
      if (paymentUpdate.paymentType) updateRequest.paymentType = paymentUpdate.paymentType;
      if (paymentUpdate.amount !== undefined) updateRequest.amount = paymentUpdate.amount;
      if (paymentUpdate.totalAmount !== undefined) updateRequest.totalAmount = paymentUpdate.totalAmount;
      if (paymentUpdate.description) updateRequest.description = paymentUpdate.description;
      if (paymentUpdate.color) updateRequest.color = paymentUpdate.color;
      if (paymentUpdate.dayOfMonth) updateRequest.dayOfMonth = paymentUpdate.dayOfMonth;
      if (paymentUpdate.firstPaymentDate) updateRequest.firstPaymentDate = paymentUpdate.firstPaymentDate;
      if (paymentUpdate.totalPayments) updateRequest.totalPayments = paymentUpdate.totalPayments;
      // Note: remainingPayments should not be sent in update requests - it's managed by the server
      if (paymentUpdate.expirationDate) updateRequest.expirationDate = paymentUpdate.expirationDate;
      if (paymentUpdate.isActive !== undefined) updateRequest.isActive = paymentUpdate.isActive;
      
      // If category is being updated, find the category ID
      if (paymentUpdate.category) {
        const category = categories.find(cat => cat.name === paymentUpdate.category);
        if (category) {
          updateRequest.categoryId = category.id;
        }
      }

      // Call the API
      const apiRecurringPayment = await recurringPaymentApi.update(id, updateRequest);
      const updatedPeriodicPayment = mapApiRecurringPaymentToPeriodicPayment(apiRecurringPayment);
      
      // Update local state
      setPeriodicPayments(prev => 
        prev.map(payment => payment.id === id ? updatedPeriodicPayment : payment)
      );
      toast.success('Periodic payment updated successfully');
    } catch (error) {
      console.error('Failed to update periodic payment:', error);
      toast.error('Failed to update periodic payment. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deletePeriodicPayment = async (id: string) => {
    if (!useApi) {
      // Fallback to mock data when API is not available
      setPeriodicPayments(prev => prev.filter(payment => payment.id !== id));
      toast.success('Periodic payment deleted successfully (offline mode)');
      return;
    }

    try {
      setLoading(true);
      
      // Call the API
      await recurringPaymentApi.delete(id);
      
      // Update local state
      setPeriodicPayments(prev => prev.filter(payment => payment.id !== id));
      toast.success('Periodic payment deleted successfully');
    } catch (error) {
      console.error('Failed to delete periodic payment:', error);
      toast.error('Failed to delete periodic payment. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async (): Promise<void> => {
    await loadData();
  };

  const loadTransactions = useCallback(async (params?: { from?: string; to?: string }): Promise<void> => {
    if (!useApi) return;
    
    // Don't load transactions if categories aren't loaded yet
    // This prevents race condition where transactions map to "Unknown" categories
    if (categories.length === 0) {
      console.log('⚠️ Categories not loaded yet, skipping transaction load');
      return;
    }
    
    setLoading(true);
    setError(null);
    setAuthError(false);

    try {
      const apiTransactions = await transactionApi.getAll(params);
      const mappedTransactions = apiTransactions.map(tx => mapApiTransactionToTransaction(tx, categories));
      setTransactions(mappedTransactions);
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('403')) {
          setAuthError(true);
          setError('Authentication failed. Please log in again.');
        } else {
          setError(`Failed to load transactions: ${err.message}`);
        }
      } else {
        setError('Failed to load transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [useApi, categories]);

  const clearAuthError = (): void => {
    setAuthError(false);
    setError(null);
  };

  const loadAllPeriodicPayments = async (): Promise<void> => {
    if (!useApi) return;
    
    try {
      const apiRecurringPayments = await recurringPaymentApi.getAll();
      const mappedPeriodicPayments = apiRecurringPayments.map(mapApiRecurringPaymentToPeriodicPayment);
      setPeriodicPayments(mappedPeriodicPayments);
    } catch (err) {
      console.error('Failed to load all periodic payments:', err);
      toast.error('Failed to load all periodic payments');
    }
  };

  const loadActivePeriodicPayments = async (): Promise<void> => {
    if (!useApi) return;
    
    try {
      const apiRecurringPayments = await recurringPaymentApi.getActive();
      const mappedPeriodicPayments = apiRecurringPayments.map(mapApiRecurringPaymentToPeriodicPayment);
      setPeriodicPayments(mappedPeriodicPayments);
    } catch (err) {
      console.error('Failed to load active periodic payments:', err);
      toast.error('Failed to load active periodic payments');
    }
  };

  const value: ExpenseContextType = {
    transactions,
    categories,
    periodicPayments,
    loading,
    error,
    authError,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    addCategory,
    updateCategory,
    deleteCategory,
    addPeriodicPayment,
    updatePeriodicPayment,
    deletePeriodicPayment,
    loadAllPeriodicPayments,
    loadActivePeriodicPayments,
    refreshData,
    loadTransactions,
    clearAuthError,
  };

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};

export const useExpense = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpense must be used within an ExpenseProvider');
  }
  return context;
};